import { TimePicker } from '@components/TimePicker';
import {
  Box,
  Checkbox,
  CollapsibleCard,
  Grid,
  Stack,
  Typography,
  DatePicker,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import {
  AutoComplete,
  CardPlaceholder,
  Popover,
  SelectInputCustom,
  usePopoverRef,
} from '@components';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import {
  ProspectAccountRepSearchResult,
  prospectDetailsStore,
  updateProspectDetails,
} from '@store/prospect';
import { CheckIcon, AlertIcon } from '@atgfr8/icons';
import { NewATGOnlyNote } from '@views/order/components';
import { SetAppReminder } from '@views/order/components/carrier/stops/stopsEditor/SetAppReminder';
import { DateTime } from 'luxon';
import { Component, For, Show } from 'solid-js';

import { ProspectDetailsAddressCard } from './ProspectDetailsAddressCard';
import { ProspectDetailsContactCard } from './ProspectDetailsContactCard';
import { ProspectDetailsUploadDocuments } from './ProspectDetailsUploadDocuments';
import { validateFieldForProspect } from './utils';

// Chooses a chevron colour that stays visible against the selected swatch:
// the default grey when nothing is selected (white trigger), a dark icon on
// light fills (e.g. yellow), and a white icon on dark fills.
const getColorSelectIconColor = (hex: string) => {
  const value = hex.replace('#', '');
  if (value.length !== 6) {
    return '#757575';
  }
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  // Perceived brightness (YIQ); light fills get a dark icon, dark fills white.
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 150 ? '#212121' : '#fff';
};

export const ProspectDetailsInformation: Component = () => {
  const [setReminderRef, closeReminderPopover] = usePopoverRef();

  const colors = [
    { value: '#3C5BFF', color: '#3C5BFF' },
    { value: '#168F00', color: '#168F00' },
    { value: '#F9F862', color: '#F9F862' },
    { value: '#F50000', color: '#F50000' },
    { value: '#000000', color: '#000000' },
  ];

  const handleFollowupDateChange = (type: string, value: string) => {
    if (type === 'date') {
      const timeExists = Boolean(
        prospectDetailsStore.prospectDetails.followUpDate,
      )
        ? prospectDetailsStore.prospectDetails.followUpDate?.split('T')[1]
        : '00:00:00';

      const time = timeExists === '00:00:00' ? '00:00:00' : timeExists;

      updateProspectDetails({
        followUpDate: `${value.split('T')[0]}T${time}`,
      });
    } else {
      const followUpDate =
        prospectDetailsStore.prospectDetails.followUpDate !== null
          ? prospectDetailsStore.prospectDetails.followUpDate.split('T')[0]
          : DateTime.now().toISO().split('T')[0];
      updateProspectDetails({
        followUpDate: `${followUpDate}T${value.split('T')[1]}:00`,
      });
    }
  };

  return (
    <Box style={{ 'flex-grow': 1 }}>
      <Grid container spacing={2}>
        <Grid item size={{ xs: 12, lg: 4 }}>
          <CollapsibleCard
            startTitle={'Prospect Information'}
            loading={prospectDetailsStore.loading}
            class="mb-2"
          >
            <Stack spacing={2}>
              <Box class="flex flex-col" gap={1}>
                <Box display="flex" alignItems="start" gap={1}>
                  <TextField
                    label="Name"
                    value={prospectDetailsStore.prospectDetails.name}
                    onChange={async (v: string) => {
                      updateProspectDetails({ name: v });
                      await validateFieldForProspect('name', 'name');
                    }}
                    maxLength={50}
                    error={
                      prospectDetailsStore.prospectDetailsErrors
                        ? prospectDetailsStore.prospectDetailsErrors['name']
                        : ''
                    }
                    dataTestId={'name-textInput'}
                  />
                  <div class="relative inline-flex flex-col min-w-0 align-top w-[90px]">
                    <SelectInputCustom
                      label="Color"
                      dataTestId="prospect-color-select"
                      size="sm"
                      iconColor={getColorSelectIconColor(
                        prospectDetailsStore.prospectDetails.color ?? '',
                      )}
                      class="w-full p-0! gap-0! overflow-hidden relative! isolate"
                      value={prospectDetailsStore.prospectDetails.color ?? ''}
                      menuItems={colors.map((option) => ({
                        value: option.value,
                        label: (
                          <div
                            class="h-6 w-full"
                            style={{ 'background-color': option.color }}
                          />
                        ),
                        class: 'p-0! min-h-6!',
                      }))}
                      renderValue={(value) => {
                        const color = Array.isArray(value)
                          ? (value[0] ?? '')
                          : value;
                        return (
                          <div
                            class="absolute inset-0 -z-10"
                            style={{ 'background-color': color }}
                          />
                        );
                      }}
                      onChange={(value) => {
                        updateProspectDetails({ color: value });
                      }}
                      menuClass="p-0!"
                      menuItemClass="p-0!"
                    />
                  </div>
                </Box>
                <Show
                  when={prospectDetailsStore.customerMatches.length > 0}
                  fallback={
                    <Box class="border border-[#d6e9c6] p-2 bg-[#dff0d8] text-[#3c763d]!">
                      <Typography
                        variant="body1"
                        class="flex! items-center! text-xs!"
                      >
                        <CheckIcon class="pr-1! size-6!" />
                        No matches found to existing ATG customers
                      </Typography>
                    </Box>
                  }
                >
                  <Box class="border border-[#faebcc] p-2 bg-[#fcf8e3] text-[#8a6d3b]!">
                    <Typography
                      variant="body1"
                      class="pb-1! flex! items-center! text-sm!"
                    >
                      <AlertIcon class="pr-1! size-6!" />
                      Potential active customer conflicts:
                    </Typography>
                    <For each={prospectDetailsStore.customerMatches}>
                      {(match) => (
                        <Typography variant="body1" class="text-xs!">
                          <strong>{match.name}</strong> (Status: {match.status})
                        </Typography>
                      )}
                    </For>
                  </Box>
                </Show>
              </Box>
              <Stack spacing={1.5}>
                <Box display="flex" alignItems="center" gap={2}>
                  <AutoComplete
                    endpoint={'/user/autocomplete?term'}
                    version="v4"
                    responseFieldName="name"
                    onItemSelect={(item) => {
                      updateProspectDetails({
                        createdBy: (item as ProspectAccountRepSearchResult)
                          .name,
                        createdById: (item as ProspectAccountRepSearchResult)
                          .id,
                      });
                    }}
                    innerRefClass="flex-1! "
                    id="autocomplete-origin-location"
                    label="Account Rep"
                    placeholder="Search Account Rep Name"
                    defaultValue=""
                    value={prospectDetailsStore.prospectDetails.createdBy ?? ''}
                    onChange={(v) => updateProspectDetails({ createdBy: v })}
                    onClearValue={() =>
                      updateProspectDetails({ createdBy: '' })
                    }
                  />
                  <Checkbox
                    size="xs"
                    color="info"
                    class="pr-3 rounded-md m-0 [&_input]:p-[5px]"
                    label="Public"
                    checked={prospectDetailsStore.prospectDetails.public}
                    onChange={(checked) => {
                      updateProspectDetails({ public: checked });
                    }}
                    dataTestId={'public-checkboxInput'}
                  />
                </Box>
                <Show
                  when={Boolean(prospectDetailsStore.prospectDetails.public)}
                >
                  <Box class="bg-[#CAE7F0] text-sm p-1 border border-[#B0C4DE]">
                    This prospect is public, meaning everyone in your office
                    will see you are working with this prospect.
                  </Box>
                </Show>
              </Stack>
            </Stack>
          </CollapsibleCard>
          <ProspectDetailsAddressCard />
          <ProspectDetailsUploadDocuments />
        </Grid>
        <Grid item size={{ xs: 12, lg: 4 }}>
          <ProspectDetailsContactCard />
          <NewATGOnlyNote
            cardTitle="Notes"
            type="Prospect"
            comments={prospectDetailsStore.prospectDetails.notes}
            maxHeight="300px"
            loading={prospectDetailsStore.loading}
            maxLength={2000}
            addChangeTrackingSource={
              CHANGE_TRACKING_SOURCES.prospectDetails.notesAdd
            }
            deleteChangeTrackingSource={
              CHANGE_TRACKING_SOURCES.prospectDetails.notesDelete
            }
          />
        </Grid>
        <Grid item size={{ xs: 12, lg: 4 }}>
          <Stack spacing={1}>
            <Show
              when={!prospectDetailsStore.loading}
              fallback={<CardPlaceholder />}
            >
              <Box display="flex" gap={0.5} mb={2} alignItems="center">
                <Typography
                  variant="body1"
                  class="text-sm! font-semibold! text-[#4A5568]!"
                >
                  Last Contact Date :
                </Typography>
                <Typography variant="body1" class="text-sm! ">
                  {Boolean(prospectDetailsStore.prospectDetails.lastContactDate)
                    ? DateTime.fromISO(
                        prospectDetailsStore.prospectDetails.lastContactDate,
                      ).toFormat('M/d/yyyy')
                    : 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Stack spacing={1} direction="row">
                  <DatePicker
                    label="Followup Date"
                    value={
                      prospectDetailsStore.prospectDetails.followUpDate ?? ''
                    }
                    handleChange={(date: string) =>
                      handleFollowupDateChange('date', date)
                    }
                    dataTestId={'followup-date-datepicker'}
                  />
                  <TimePicker
                    label="Followup Time"
                    value={
                      prospectDetailsStore.prospectDetails.followUpDate?.split(
                        'T',
                      )[1] ?? ''
                    }
                    onChange={(time) => {
                      handleFollowupDateChange('time', time);
                    }}
                    dataTestId={'followup-time-timepicker'}
                  />
                </Stack>
                <Popover
                  ref={setReminderRef}
                  eleName="Set Reminder"
                  sxProps={{
                    'background-color': 'white',
                    'border-radius': '0',
                    'text-decoration': 'underline !important',
                  }}
                >
                  <SetAppReminder
                    type="Prospect"
                    onSubmit={closeReminderPopover}
                    changeTrackingSource={
                      CHANGE_TRACKING_SOURCES.prospectDetails
                        .followUpSetReminder
                    }
                  />
                </Popover>
              </Box>
            </Show>
            <NewATGOnlyNote
              cardTitle="Call Log"
              type="Call Log"
              comments={prospectDetailsStore.prospectDetails.callLog}
              loading={prospectDetailsStore.loading}
              maxHeight="300px"
              maxLength={2000}
              addChangeTrackingSource={
                CHANGE_TRACKING_SOURCES.prospectDetails.callLogAdd
              }
              deleteChangeTrackingSource={
                CHANGE_TRACKING_SOURCES.prospectDetails.callLogDelete
              }
            />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};
